/**
 * Overlap image blocks (desktop only): unlock overflow on ancestors and scale
 * the image so it extends past the parent group top/bottom.
 */

/** @type {WeakMap<HTMLElement, ResizeObserver>} */
const overlapObservers = new WeakMap();

const mediaQueryDesktop = window.matchMedia('(min-width: 750px)');

function teardownImageBlockOverlap() {
  document.querySelectorAll('.image-block--overlap-section').forEach((imageBlock) => {
    imageBlock.style.removeProperty('--overlap-scale');
    imageBlock.removeAttribute('data-overlap-init');
    imageBlock.removeAttribute('data-overlap-observed');

    const observer = overlapObservers.get(imageBlock);
    if (observer) {
      observer.disconnect();
      overlapObservers.delete(imageBlock);
    }
  });

  document.querySelectorAll('.section--overlap-image').forEach((node) => {
    node.classList.remove('section--overlap-image');
  });

  document.querySelectorAll('.group-block--overlap-image').forEach((node) => {
    node.classList.remove('group-block--overlap-image');
  });
}

function initImageBlockOverlap() {
  if (!mediaQueryDesktop.matches) {
    teardownImageBlockOverlap();
    return;
  }

  document.querySelectorAll('.image-block--overlap-section:not([data-overlap-init])').forEach((imageBlock) => {
    imageBlock.setAttribute('data-overlap-init', '');

    const section = imageBlock.closest('.shopify-section');
    let outerGroup = imageBlock.closest('.group-block');

    if (outerGroup && section) {
      let parentGroup = outerGroup.parentElement?.closest('.group-block');

      while (parentGroup && section.contains(parentGroup)) {
        outerGroup = parentGroup;
        parentGroup = parentGroup.parentElement?.closest('.group-block');
      }
    }

    const ancestors = [section, section?.querySelector('.section')];

    let group = imageBlock.closest('.group-block');
    while (group && section?.contains(group)) {
      ancestors.push(group);
      group = group.parentElement?.closest('.group-block');
    }

    ancestors.forEach((node) => {
      node?.classList.add('section--overlap-image');
    });

    section?.querySelector('.custom-section-content')?.classList.add('section--overlap-image');
    section?.querySelector('.section-content-wrapper')?.classList.add('section--overlap-image');

    outerGroup?.classList.add('group-block--overlap-image');
    observeOverlapImage(imageBlock, outerGroup);

    if (!imageBlock.dataset.overlapObserved) {
      imageBlock.dataset.overlapObserved = 'true';

      const resizeObserver = new ResizeObserver(() => {
        updateOverlapScale(imageBlock, outerGroup);
      });

      if (outerGroup) resizeObserver.observe(outerGroup);
      resizeObserver.observe(imageBlock);
      overlapObservers.set(imageBlock, resizeObserver);
    }
  });
}

/**
 * @param {HTMLElement} imageBlock
 * @param {HTMLElement | null | undefined} outerGroup
 */
function updateOverlapScale(imageBlock, outerGroup) {
  if (!mediaQueryDesktop.matches) return;

  if (!outerGroup) {
    imageBlock.style.removeProperty('--overlap-scale');
    return;
  }

  const rowHeight = outerGroup.offsetHeight;
  const imageHeight = imageBlock.offsetHeight;

  if (rowHeight <= 0 || imageHeight <= 0) return;

  const bleed = Math.min(80, Math.max(32, rowHeight * 0.12));
  const targetHeight = rowHeight + bleed * 2;
  const scale = Math.max(targetHeight / imageHeight, 1.08);

  imageBlock.style.setProperty('--overlap-scale', scale.toFixed(3));
}

function observeOverlapImage(imageBlock, outerGroup) {
  updateOverlapScale(imageBlock, outerGroup);

  imageBlock.querySelector('img')?.addEventListener(
    'load',
    () => updateOverlapScale(imageBlock, outerGroup),
    { once: true }
  );
}

mediaQueryDesktop.addEventListener('change', initImageBlockOverlap);
document.addEventListener('DOMContentLoaded', initImageBlockOverlap);
document.addEventListener('shopify:section:load', initImageBlockOverlap);
document.addEventListener('shopify:block:select', initImageBlockOverlap);

if (document.readyState !== 'loading') {
  initImageBlockOverlap();
}
